import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, fetchConfig, isDesktopBreakpoint, mediaQueryLarge, onAnimationEnd } from '@theme/utilities';
import {
  ThemeEvents,
  VariantSelectedEvent,
  VariantUpdateEvent,
  SlideshowSelectEvent,
  CartAddEvent,
} from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false',
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];
  /** @type {AbortController | undefined} */
  #dstrezzedSwatchAbortController;
  /** @type {AbortController | undefined} */
  #sizePanelAbortController;
  /** @type {MediaQueryList} */
  #dstrezzedCardViewMq = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  get #sizePanel() {
    const panel = this.querySelector('[data-product-card-size-panel]');
    return panel instanceof HTMLDialogElement ? panel : null;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('click', this.#handleDstrezzedColorSwatchClick);
    this.addEventListener('pointerover', this.#handleDstrezzedColorSwatchHover);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);
    this.#dstrezzedCardViewMq.addEventListener('change', this.#handleDstrezzedCardViewMqChange);

    this.addEventListener('click', this.navigateToProduct);
    this.#bindSizePanel();

    this.#syncDstrezzedCardView();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dstrezzedSwatchAbortController?.abort();
    this.#sizePanelAbortController?.abort();
    this.removeEventListener('click', this.#handleDstrezzedColorSwatchClick);
    this.removeEventListener('pointerover', this.#handleDstrezzedColorSwatchHover);
    this.removeEventListener('click', this.navigateToProduct);
    this.#dstrezzedCardViewMq.removeEventListener('change', this.#handleDstrezzedCardViewMqChange);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  #handleDstrezzedCardViewMqChange = () => {
    this.#syncDstrezzedCardView();
  };

  /**
   * Applies Model/Product overlay + slideshow lead visibility for Dstrezzed cards.
   * Desktop Model mode hides both slideshow leads (overlay owns Model/Product).
   * Desktop Product mode hides only the Product lead and keeps Model visible so hover
   * reveals Model then remaining images. Mobile Product mode keeps both leads visible
   * (Product first, Model second); Mobile Model mode shows only the Model lead.
   * Overlay-only cards keep leads hidden on every breakpoint.
   * @param {{ resetSlideshow?: boolean }} [options]
   */
  #syncDstrezzedCardView({ resetSlideshow = true } = {}) {
    const gallery = this.refs.cardGallery;
    if (!(gallery instanceof HTMLElement) || !gallery.classList.contains('card-gallery--dstrezzed-product')) {
      return;
    }

    const wrapper = this.closest('.collection-wrapper');
    const wrapperView = wrapper?.getAttribute('product-card-view');
    /** @type {'model' | 'product'} */
    let view = 'product';
    if (wrapperView === 'model' || wrapperView === 'product') {
      view = wrapperView;
    } else if (gallery.getAttribute('data-initial-shown-image') === 'model') {
      view = 'model';
    }

    const overlayOnly = Boolean(
      gallery.querySelector(':scope > .card-gallery__view-overlay--standalone') ||
        gallery.querySelector('slideshow-component[data-dstrezzed-overlay-only]')
    );
    const isDesktopCardView = this.#dstrezzedCardViewMq.matches;
    this.#applyDstrezzedLeadVisibility(gallery, view, { overlayOnly, isDesktopCardView });

    if (overlayOnly) return;

    this.#pinDstrezzedInitialSlide({ overlayOnly, isDesktopCardView, view });

    if (!resetSlideshow) return;

    this.#selectFirstDstrezzedContentSlide();

    // Slideshow setup can later select initial-slide and add [reveal] on a lead — re-apply after that.
    requestAnimationFrame(() => {
      this.#applyDstrezzedLeadVisibility(gallery, view, { overlayOnly, isDesktopCardView });
      this.#pinDstrezzedInitialSlide({ overlayOnly, isDesktopCardView, view });
      this.#selectFirstDstrezzedContentSlide();
    });
  }

  /**
   * @param {HTMLElement} gallery
   * @param {'model' | 'product'} view
   * @param {{ overlayOnly: boolean, isDesktopCardView: boolean }} options
   */
  #applyDstrezzedLeadVisibility(gallery, view, { overlayOnly, isDesktopCardView }) {
    for (const image of gallery.querySelectorAll('.card-gallery__view-image[data-card-view]')) {
      if (!(image instanceof HTMLElement)) continue;

      const cardView = image.dataset.cardView;
      if (cardView !== 'model' && cardView !== 'product') continue;

      image.toggleAttribute('hidden', cardView !== view);
    }

    for (const slide of gallery.querySelectorAll('slideshow-slide[data-card-view]')) {
      if (!(slide instanceof HTMLElement)) continue;

      const cardView = slide.dataset.cardView;
      if (cardView !== 'model' && cardView !== 'product') continue;

      let hideLead = false;
      if (overlayOnly) {
        hideLead = true;
      } else if (isDesktopCardView) {
        // Product mode: keep Model in the track for hover; Model mode: both leads stay under overlay.
        hideLead = view === 'product' ? cardView === 'product' : true;
      } else if (view === 'product') {
        // Mobile Product: Product @0 and Model @1 both swipeable.
        hideLead = false;
      } else {
        // Mobile Model: only the Model lead is shown.
        hideLead = cardView !== 'model';
      }

      slide.toggleAttribute('hidden', hideLead);
      slide.removeAttribute('reveal');
    }
  }

  /**
   * Pin slideshow[initial-slide] past hidden leads so setup cannot re-reveal them.
   * Product @0, Model @1. Desktop Product mode pins to Model (visible under overlay).
   * @param {{ overlayOnly: boolean, isDesktopCardView: boolean, view: 'model' | 'product' }} options
   */
  #pinDstrezzedInitialSlide({ overlayOnly, isDesktopCardView, view }) {
    const slideshow = this.refs.slideshow;
    if (!(slideshow instanceof HTMLElement)) return;

    const slides = slideshow.querySelectorAll(':scope slideshow-slide');
    if (!slides.length) return;

    let initialIndex = view === 'product' ? 0 : 1;

    if (overlayOnly) {
      initialIndex = view === 'product' ? 0 : Math.min(1, slides.length - 1);
    } else if (isDesktopCardView) {
      initialIndex = 0;
      for (const [index, slide] of slides.entries()) {
        if (!(slide instanceof HTMLElement)) continue;

        if (view === 'product') {
          // Prefer Model lead, then any non-lead content slide.
          if (slide.dataset.cardView === 'model' || !slide.hasAttribute('data-card-view')) {
            initialIndex = index;
            break;
          }
          continue;
        }

        if (slide.hasAttribute('data-card-view')) continue;
        initialIndex = index;
        break;
      }
    }

    slideshow.setAttribute('initial-slide', String(initialIndex));
  }

  #selectFirstDstrezzedContentSlide() {
    const slideshow = this.refs.slideshow;
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const firstVisible = slideshow.slides?.[0];
    if (!(firstVisible instanceof HTMLElement)) return;

    const slideId = firstVisible.getAttribute('slide-id');
    if (!slideId) return;

    slideshow.select({ id: slideId }, undefined, { animate: false, force: true });
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#updateDstrezzedProductBadges(event.detail.data.html);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        }),
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    // Cards may have one price (Dstrezzed) or historically two; always morph the last match.
    const priceContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
    const priceContainer = priceContainers[priceContainers.length - 1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Syncs sale / sold-out / NEW IN badges with the selected color variant.
   * @param {Document} html
   */
  #updateDstrezzedProductBadges(html) {
    const cardGallery = this.refs.cardGallery;
    if (!(cardGallery instanceof HTMLElement)) return;

    const currentBadges = cardGallery.querySelector('.product-badges');
    const updatedCard = this.#querySectionRenderingProductCard(html);
    const updatedBadges = updatedCard?.querySelector('.product-badges') || html.querySelector('product-card .product-badges');

    if (currentBadges instanceof HTMLElement && updatedBadges instanceof HTMLElement) {
      morph(currentBadges, updatedBadges);
      return;
    }

    if (!(currentBadges instanceof HTMLElement) && updatedBadges instanceof HTMLElement) {
      cardGallery.prepend(updatedBadges.cloneNode(true));
      return;
    }

    if (currentBadges instanceof HTMLElement && !(updatedBadges instanceof HTMLElement)) {
      currentBadges.remove();
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Handles Dstrezzed color swatch clicks.
   * Keeps interaction inside product card and updates size selector variants.
   * @param {MouseEvent} event
   */
  #handleDstrezzedColorSwatchClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const overflowButton = /** @type {HTMLButtonElement | null} */ (
      event.target.closest('.product-card-dstrezzed__color-swatch-overflow')
    );
    if (overflowButton) {
      event.preventDefault();
      event.stopPropagation();
      this.#revealDstrezzedColorSwatches(overflowButton);
      return;
    }

    const swatchButton = /** @type {HTMLButtonElement | null} */ (
      event.target.closest('.product-card-dstrezzed__color-swatch')
    );
    if (!swatchButton) return;

    event.preventDefault();
    this.#selectDstrezzedColorSwatch(swatchButton);
  };

  /**
   * When `data-color-swatch-hover-action` is set, fine-pointer hover selects a color.
   * Touch / coarse pointers keep click-only behavior.
   * @param {PointerEvent} event
   */
  #handleDstrezzedColorSwatchHover = (event) => {
    if (event.pointerType === 'touch' || event.pointerType === 'pen') return;
    if (!this.#dstrezzedCardViewMq.matches) return;
    if (!(event.target instanceof Element)) return;

    const swatchButton = /** @type {HTMLButtonElement | null} */ (
      event.target.closest('.product-card-dstrezzed__color-swatch')
    );
    if (!swatchButton || !this.contains(swatchButton)) return;

    const related = event.relatedTarget;
    if (related instanceof Node && swatchButton.contains(related)) return;

    const wrapper = swatchButton.closest('.product-card-dstrezzed__color-swatches-wrapper');
    if (!(wrapper instanceof HTMLElement) || !wrapper.hasAttribute('data-color-swatch-hover-action')) return;

    const expandedRow = swatchButton.closest('.product-card-dstrezzed__color-swatches--expanded');
    if (expandedRow?.classList.contains('is-dragging')) return;

    if (swatchButton.getAttribute('data-selected') === 'true') return;

    this.#selectDstrezzedColorSwatch(swatchButton);
  };

  /**
   * Applies selected color swatch state and refreshes card content for that color.
   * Sibling-product swatches reload title, gallery, price, and variants from that product.
   * @param {HTMLButtonElement} swatchButton
   */
  #selectDstrezzedColorSwatch(swatchButton) {
    const colorValue = swatchButton.dataset.colorValue;
    const colorVariantId = swatchButton.dataset.colorVariantId;
    if (!colorValue) return;

    const swatches = this.querySelectorAll('.product-card-dstrezzed__color-swatch');
    for (const swatch of swatches) {
      swatch.removeAttribute('data-selected');
      swatch.setAttribute('aria-pressed', 'false');
    }

    swatchButton.setAttribute('data-selected', 'true');
    swatchButton.setAttribute('aria-pressed', 'true');

    const productData = this.#getSwatchProductData(swatchButton);
    if (productData) {
      this.#applyDstrezzedSwatchProductData(productData, colorValue);
    }

    this.#updateDstrezzedSizeSelector(colorValue);
    this.#updateDstrezzedMobileQuickAdd(colorValue, colorVariantId);
    this.#updateSizePanelSelectedColor(colorValue, colorVariantId);
    if (colorVariantId) {
      void this.#updateDstrezzedCardForColor(
        colorVariantId,
        colorValue,
        productData?.url || swatchButton.dataset.productUrl,
      );
    }
  }

  /**
   * @typedef {Object} DstrezzedSwatchVariantData
   * @property {number} id
   * @property {boolean} available
   * @property {string|null} option1
   * @property {string|null} option2
   * @property {string|null} option3
   * @property {string[]} [options]
   * @property {boolean} [isHiddenSize]
   * @property {string|null} [featured_image]
   */

  /**
   * @typedef {Object} DstrezzedSwatchProductData
   * @property {number} id
   * @property {string} title
   * @property {string|null} [subtitle]
   * @property {string} url
   * @property {number|null} [colorOptionPosition]
   * @property {number|null} [sizeOptionPosition]
   * @property {DstrezzedSwatchVariantData[]} variants
   */

  /**
   * @returns {Record<string, DstrezzedSwatchProductData>|null}
   */
  #getSwatchProductsData() {
    const script = this.querySelector('[data-product-card-swatch-products]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLButtonElement} swatchButton
   * @returns {DstrezzedSwatchProductData|null}
   */
  #getSwatchProductData(swatchButton) {
    const productId = swatchButton.dataset.productId;
    if (!productId) return null;

    return this.#getSwatchProductsData()?.[productId] || null;
  }

  /**
   * Swaps card copy and variant payloads when a sibling color is selected.
   * @param {DstrezzedSwatchProductData} productData
   * @param {string} colorValue
   */
  #applyDstrezzedSwatchProductData(productData, colorValue) {
    this.dataset.productId = String(productData.id);

    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    const hiddenTitle = productCardLink?.querySelector('.visually-hidden');
    if (hiddenTitle) {
      hiddenTitle.textContent = productData.title;
    }

    if (productTitleLink instanceof HTMLElement) {
      const titleText = productTitleLink.querySelector('p') || productTitleLink.querySelector('.title-text');
      if (titleText) {
        titleText.textContent = productData.title;
      }
    }

    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.setAttribute('aria-label', productData.title);
    }

    const subtitle = this.querySelector('.product-card-subtitle p');
    if (subtitle instanceof HTMLElement) {
      subtitle.textContent = productData.subtitle || '';
      const subtitleWrap = subtitle.closest('.product-card-subtitle');
      if (subtitleWrap instanceof HTMLElement) {
        subtitleWrap.hidden = !productData.subtitle;
      }
    }

    const wishlist = this.querySelector('.product-card-dstrezzed__wishlist');
    if (wishlist instanceof HTMLAnchorElement) {
      wishlist.setAttribute('aria-label', `Wishlist ${productData.title}`);
    }

    const selector = this.querySelector('.product-card-dstrezzed__size-selector');
    if (selector instanceof HTMLElement) {
      if (productData.colorOptionPosition) {
        selector.dataset.colorOptionPosition = String(productData.colorOptionPosition);
      }
      if (productData.sizeOptionPosition) {
        selector.dataset.sizeOptionPosition = String(productData.sizeOptionPosition);
      }
      selector.dataset.selectedColor = colorValue;

      const variantsScript = selector.querySelector('.product-card-dstrezzed__variants-data');
      if (variantsScript instanceof HTMLScriptElement) {
        variantsScript.textContent = JSON.stringify(productData.variants);
      }

      for (const sizeForm of selector.querySelectorAll('.product-card-dstrezzed__size-form')) {
        if (sizeForm instanceof HTMLElement) {
          sizeForm.dataset.productId = String(productData.id);
        }
      }
    }

    const quickAddForm = this.querySelector('.product-card-dstrezzed__mobile-quick-add-form');
    if (quickAddForm instanceof HTMLElement) {
      quickAddForm.dataset.productId = String(productData.id);
    }

    const panel = this.#sizePanel;
    if (panel instanceof HTMLDialogElement) {
      panel.dataset.productId = String(productData.id);
      if (productData.colorOptionPosition) {
        panel.dataset.colorOptionPosition = String(productData.colorOptionPosition);
      }
      panel.dataset.selectedColor = colorValue;

      const panelTitle = panel.querySelector('.product-card-dstrezzed__size-panel-title');
      if (panelTitle) {
        panelTitle.textContent = productData.title;
      }

      const panelItem = panel.querySelector('[data-product-card-size-panel-item]');
      if (panelItem instanceof HTMLElement) {
        panelItem.dataset.productCardSizePanelProductId = String(productData.id);
      }

      if (productData.sizeOptionPosition) {
        const zeroBasedSizePosition = String(productData.sizeOptionPosition - 1);
        panel.dataset.sizeOptionPosition = zeroBasedSizePosition;
        for (const sizeControl of panel.querySelectorAll('[data-product-card-size-option], [data-size-axis]')) {
          if (sizeControl instanceof HTMLElement) {
            sizeControl.dataset.sizeOptionPosition = zeroBasedSizePosition;
          }
        }
      }

      const panelVariants = panel.querySelector('[data-product-card-size-panel-variants]');
      if (panelVariants instanceof HTMLScriptElement) {
        panelVariants.textContent = JSON.stringify(productData.variants);
      }
    }
  }

  /**
   * @param {Document} html
   * @returns {Element|null}
   */
  #querySectionRenderingProductCard(html) {
    const productId = this.dataset.productId;
    if (productId) {
      const matched = html.querySelector(`product-card[data-product-id="${productId}"]`);
      if (matched) return matched;
    }

    return html.querySelector('product-card');
  }

  #bindSizePanel() {
    this.#sizePanelAbortController?.abort();
    this.#sizePanelAbortController = new AbortController();
    const { signal } = this.#sizePanelAbortController;

    this.addEventListener('click', this.#handleQuickAddLeadToPdpClick, { capture: true, signal });
    this.addEventListener('click', this.#handleSizePanelClick, { capture: true, signal });
    this.addEventListener('change', this.#handleSizePanelChange, { signal });

    const panel = this.#sizePanel;
    if (panel) {
      panel.addEventListener('close', this.#handleSizePanelClose, { signal });
      panel.addEventListener('cancel', this.#handleSizePanelCancel, { signal });
    }

    this.#wireMobileQuickAddTrigger();
    this.#syncQuickAddLeadToPdpControls();
  }

  get #quickAddLeadsToPdp() {
    return this.hasAttribute('data-quick-add-lead-to-pdp');
  }

  /**
   * @param {string | number | null | undefined} variantId
   * @returns {URL | null}
   */
  #variantProductUrl(variantId) {
    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement) || !link.href) return null;

    try {
      const url = new URL(link.href, window.location.origin);
      if (variantId) {
        url.searchParams.set('variant', String(variantId));
      }
      return url;
    } catch {
      return null;
    }
  }

  /**
   * First available variant for the selected color, else the first available variant overall.
   * @returns {string}
   */
  #getFirstAvailableVariantId() {
    const selector = this.querySelector('.product-card-dstrezzed__size-selector');
    const variantsScript = selector?.querySelector('.product-card-dstrezzed__variants-data');
    const quickAddForm = this.querySelector('.product-card-dstrezzed__mobile-quick-add-form');
    const fallbackInput = /** @type {HTMLInputElement | null} */ (quickAddForm?.querySelector('input[name="id"]'));
    const fallbackId = fallbackInput?.value || '';

    if (!(selector instanceof HTMLElement) || !(variantsScript instanceof HTMLScriptElement) || !variantsScript.textContent) {
      return fallbackId;
    }

    /** @type {Array<{id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null, isHiddenSize?: boolean}>} */
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch {
      return fallbackId;
    }

    const optionAtPosition = (
      /** @type {{option1: string|null, option2: string|null, option3: string|null}} */ variant,
      /** @type {number} */ position,
    ) => {
      if (position === 1) return variant.option1;
      if (position === 2) return variant.option2;
      if (position === 3) return variant.option3;
      return null;
    };

    const selectedColor = selector.dataset.selectedColor || '';
    const colorOptionPosition = Number.parseInt(selector.dataset.colorOptionPosition || '', 10);
    const colorMatches = variants.filter((variant) => {
      if (variant.isHiddenSize) return false;
      if (!selectedColor || Number.isNaN(colorOptionPosition)) return true;
      return optionAtPosition(variant, colorOptionPosition) === selectedColor;
    });
    const selectedVariant =
      colorMatches.find((variant) => variant.available) ||
      colorMatches[0] ||
      variants.find((variant) => !variant.isHiddenSize && variant.available) ||
      variants.find((variant) => !variant.isHiddenSize) ||
      variants[0];

    return selectedVariant ? String(selectedVariant.id) : fallbackId;
  }

  #syncQuickAddLeadToPdpControls() {
    if (!this.#quickAddLeadsToPdp) return;

    const sizeButtons = this.querySelectorAll('.product-card-dstrezzed__size-form .product-card-dstrezzed__size-button');
    for (const sizeButton of sizeButtons) {
      if (!(sizeButton instanceof HTMLButtonElement)) continue;

      sizeButton.disabled = false;
      const ariaLabel = sizeButton.getAttribute('aria-label') || '';
      if (ariaLabel.startsWith('Add size ')) {
        sizeButton.setAttribute('aria-label', ariaLabel.replace('Add size ', 'View size '));
      }
    }

    const quickAddButton = this.querySelector('.product-card-dstrezzed__mobile-quick-add');
    if (quickAddButton instanceof HTMLButtonElement) {
      quickAddButton.disabled = false;
      const productTitle = this.refs.productCardLink?.querySelector('.visually-hidden')?.textContent?.trim();
      if (productTitle) {
        quickAddButton.setAttribute('aria-label', `View ${productTitle}`);
      }
    }
  }

  /**
   * Desktop size buttons go to the selected variant PDP; mobile + goes to the first available variant.
   * @param {Event} event
   */
  #handleQuickAddLeadToPdpClick = (event) => {
    if (!this.#quickAddLeadsToPdp) return;
    if (!(event.target instanceof Element)) return;

    const sizeButton = event.target.closest('.product-card-dstrezzed__size-button');
    if (sizeButton instanceof HTMLElement && sizeButton.closest('.product-card-dstrezzed__size-form')) {
      if (sizeButton.hasAttribute('data-missing-variant')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const variantInput = /** @type {HTMLInputElement | null} */ (
        sizeButton.closest('.product-card-dstrezzed__size-form')?.querySelector('input[name="id"]')
      );
      const url = this.#variantProductUrl(variantInput?.value);
      if (!url) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.#navigateToURL(event, url);
      return;
    }

    const quickAddButton = event.target.closest('.product-card-dstrezzed__mobile-quick-add');
    if (!(quickAddButton instanceof HTMLElement)) return;

    const url = this.#variantProductUrl(this.#getFirstAvailableVariantId());
    if (!url) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#navigateToURL(event, url);
  };

  #wireMobileQuickAddTrigger() {
    if (!this.hasAttribute('data-has-size-panel')) return;

    const panel = this.#sizePanel;
    const quickAddButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.product-card-dstrezzed__mobile-quick-add')
    );
    if (!(panel instanceof HTMLDialogElement) || !quickAddButton) return;

    quickAddButton.setAttribute('type', 'button');
    quickAddButton.setAttribute('aria-haspopup', 'dialog');
    quickAddButton.setAttribute('aria-controls', panel.id);
    quickAddButton.setAttribute('aria-expanded', 'false');
    quickAddButton.setAttribute('data-puppet', 'true');
    quickAddButton.removeAttribute('name');
    quickAddButton.removeAttribute('data-added');
  }

  /**
   * @param {Event} event
   */
  #handleSizePanelClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest('[data-product-card-size-panel-close]');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.#closeSizePanel();
      return;
    }

    const panelSubmit = event.target.closest('[data-product-card-size-panel-submit]');
    if (panelSubmit instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      void this.#addSizePanelToCart(panelSubmit);
      return;
    }

    if (!this.hasAttribute('data-has-size-panel')) return;

    const quickAddButton = event.target.closest('.product-card-dstrezzed__mobile-quick-add');
    if (!quickAddButton) return;

    event.preventDefault();
    event.stopPropagation();
    this.#openSizePanel();
  };

  /**
   * @param {Event} event
   */
  #handleSizePanelChange = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-product-card-size-panel]')) return;
    if (!(event.target instanceof HTMLSelectElement)) return;

    if (
      !event.target.hasAttribute('data-size-axis') &&
      !event.target.hasAttribute('data-product-card-size-option')
    ) {
      return;
    }

    this.#syncSizePanelVariant();
  };

  #handleSizePanelClose = () => {
    const quickAddButton = this.querySelector('.product-card-dstrezzed__mobile-quick-add');
    if (quickAddButton instanceof HTMLElement) {
      quickAddButton.setAttribute('aria-expanded', 'false');
    }
  };

  /**
   * @param {Event} event
   */
  #handleSizePanelCancel = (event) => {
    event.preventDefault();
    void this.#closeSizePanel();
  };

  #openSizePanel() {
    const panel = this.#sizePanel;
    if (!(panel instanceof HTMLDialogElement) || panel.open) return;

    const selector = this.querySelector('.product-card-dstrezzed__size-selector');
    const selectedColor =
      (selector instanceof HTMLElement && selector.dataset.selectedColor) || panel.dataset.selectedColor || '';
    if (selectedColor) {
      panel.dataset.selectedColor = selectedColor;
    }

    const submitButton = panel.querySelector('[data-product-card-size-panel-submit]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.removeAttribute('aria-busy');
    }

    this.#syncSizePanelVariant();

    panel.classList.remove('dialog-closing');
    panel.style.animation = 'none';

    const surface = panel.querySelector('.product-card-dstrezzed__size-panel-surface');
    const backdrop = panel.querySelector('.product-card-dstrezzed__size-panel-backdrop');
    if (surface instanceof HTMLElement) surface.style.animation = 'none';
    if (backdrop instanceof HTMLElement) backdrop.style.animation = 'none';

    if (typeof panel.showModal === 'function') {
      panel.showModal();
    } else {
      panel.setAttribute('open', '');
    }

    // Force a reflow so open keyframes restart cleanly (same pattern as dialog.js).
    void panel.offsetWidth;
    panel.style.animation = '';
    if (surface instanceof HTMLElement) surface.style.animation = '';
    if (backdrop instanceof HTMLElement) backdrop.style.animation = '';

    const quickAddButton = this.querySelector('.product-card-dstrezzed__mobile-quick-add');
    if (quickAddButton instanceof HTMLElement) {
      quickAddButton.setAttribute('aria-expanded', 'true');
    }
  }

  async #closeSizePanel() {
    const panel = this.#sizePanel;
    if (!(panel instanceof HTMLDialogElement) || !panel.open) return;
    if (panel.classList.contains('dialog-closing')) return;

    const surface = panel.querySelector('.product-card-dstrezzed__size-panel-surface');
    const backdrop = panel.querySelector('.product-card-dstrezzed__size-panel-backdrop');

    panel.style.animation = 'none';
    if (surface instanceof HTMLElement) surface.style.animation = 'none';
    if (backdrop instanceof HTMLElement) backdrop.style.animation = 'none';
    void panel.offsetWidth;

    panel.classList.add('dialog-closing');
    panel.style.animation = '';
    if (surface instanceof HTMLElement) surface.style.animation = '';
    if (backdrop instanceof HTMLElement) backdrop.style.animation = '';

    await onAnimationEnd(surface instanceof HTMLElement ? surface : panel, undefined, {
      subtree: false,
    });

    if (typeof panel.close === 'function') {
      panel.close();
    } else {
      panel.removeAttribute('open');
    }

    panel.classList.remove('dialog-closing');

    const quickAddButton = this.querySelector('.product-card-dstrezzed__mobile-quick-add');
    if (quickAddButton instanceof HTMLElement) {
      quickAddButton.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * @param {string} colorValue
   * @param {string} [colorVariantId]
   */
  #updateSizePanelSelectedColor(colorValue, colorVariantId) {
    const panel = this.#sizePanel;
    if (!(panel instanceof HTMLDialogElement)) return;

    panel.dataset.selectedColor = colorValue;
    if (colorVariantId) {
      panel.dataset.selectedColorVariantId = colorVariantId;
    }
    this.#syncSizePanelVariant();
    this.#updateSizePanelMedia(colorValue, colorVariantId);
  }

  /**
   * Prefer the matched size+color variant image; fall back to any image for the selected color.
   * @param {string} [colorValue]
   * @param {string} [colorVariantId]
   */
  #updateSizePanelMedia(colorValue, colorVariantId) {
    const panel = this.#sizePanel;
    if (!(panel instanceof HTMLDialogElement)) return;

    const item = panel.querySelector('[data-product-card-size-panel-item]');
    const media = panel.querySelector('.product-card-dstrezzed__size-panel-media');
    const image = media?.querySelector('img, .product-card-dstrezzed__size-panel-image');
    const variantsScript = item?.querySelector('[data-product-card-size-panel-variants]');

    if (!(image instanceof HTMLImageElement) || !(variantsScript instanceof HTMLScriptElement)) return;

    /** @type {Array<{id: number, featured_image?: string|null, option1: string|null, option2: string|null, option3: string|null, options?: string[]}>} */
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent || '[]');
    } catch {
      return;
    }

    const selectedColor = colorValue || panel.dataset.selectedColor || '';
    const colorOptionPosition = Number.parseInt(panel.dataset.colorOptionPosition || '', 10);
    const variantInput = item?.querySelector('[data-product-card-size-panel-variant-id]');
    const currentVariantId =
      (variantInput instanceof HTMLInputElement && variantInput.value) ||
      colorVariantId ||
      panel.dataset.selectedColorVariantId ||
      '';

    const matchesSelectedColor = (
      /** @type {{option1: string|null, option2: string|null, option3: string|null, options?: string[]}} */ variant,
    ) => {
      if (!selectedColor || Number.isNaN(colorOptionPosition) || colorOptionPosition < 1) return true;
      return this.#getSizePanelOptionValue(variant, colorOptionPosition - 1) === selectedColor;
    };

    const currentVariant = variants.find((variant) => String(variant.id) === String(currentVariantId));
    let imageUrl =
      currentVariant && matchesSelectedColor(currentVariant) ? currentVariant.featured_image || null : null;

    if (!imageUrl && selectedColor && !Number.isNaN(colorOptionPosition) && colorOptionPosition > 0) {
      imageUrl =
        variants.find((variant) => Boolean(variant.featured_image) && matchesSelectedColor(variant))
          ?.featured_image || null;
    }

    if (!imageUrl && colorVariantId) {
      imageUrl = variants.find((variant) => String(variant.id) === String(colorVariantId))?.featured_image || null;
    }

    if (!imageUrl) {
      imageUrl = currentVariant?.featured_image || null;
    }

    if (!imageUrl) return;

    const widths = [360, 540, 720, 900];
    const srcset = widths
      .map((width) => {
        const sizedUrl = imageUrl.includes('width=')
          ? imageUrl.replace(/width=\d+/g, `width=${width}`)
          : `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=${width}`;
        return `${sizedUrl} ${width}w`;
      })
      .join(', ');

    image.src = imageUrl;
    image.srcset = srcset;
    image.sizes = '50vw';
  }

  /**
   * @param {HTMLElement} item
   * @param {number} zeroBasedIndex
   * @param {{option1: string|null, option2: string|null, option3: string|null, options?: string[]}} variant
   * @returns {string | null}
   */
  #getSizePanelOptionValue(variant, zeroBasedIndex) {
    if (Array.isArray(variant.options) && variant.options[zeroBasedIndex] != null) {
      return String(variant.options[zeroBasedIndex]);
    }

    if (zeroBasedIndex === 0) return variant.option1;
    if (zeroBasedIndex === 1) return variant.option2;
    if (zeroBasedIndex === 2) return variant.option3;
    return null;
  }

  /**
   * @param {HTMLElement} item
   * @param {Array<{id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null, options?: string[]}>} variants
   * @returns {object | undefined}
   */
  #findSizePanelSplitVariant(item, variants) {
    const panel = this.#sizePanel;
    const waistSelect = item.querySelector('[data-size-axis="waist"]');
    const lengthSelect = item.querySelector('[data-size-axis="length"]');

    if (!(waistSelect instanceof HTMLSelectElement) || !(lengthSelect instanceof HTMLSelectElement)) {
      return undefined;
    }

    this.#syncSizePanelOptionWidth(waistSelect);
    this.#syncSizePanelOptionWidth(lengthSelect);

    const waist = waistSelect.value.trim();
    const length = lengthSelect.value.trim();
    const sizeOptionPosition = Number.parseInt(
      waistSelect.dataset.sizeOptionPosition || panel?.dataset.sizeOptionPosition || '0',
      10,
    );
    const colorOptionPosition = Number.parseInt(panel?.dataset.colorOptionPosition || '', 10);
    const selectedColor = panel?.dataset.selectedColor || '';

    return variants.find((variant) => {
      if (variant.isHiddenSize) return false;

      const optionValue = this.#getSizePanelOptionValue(variant, sizeOptionPosition);
      if (optionValue == null) return false;

      const parts = optionValue.split('/').map((part) => part.trim());
      if (parts[0] !== waist || parts[1] !== length) return false;

      if (!Number.isNaN(colorOptionPosition) && colorOptionPosition > 0 && selectedColor) {
        return this.#getSizePanelOptionValue(variant, colorOptionPosition - 1) === selectedColor;
      }

      return true;
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {Array<{id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null, options?: string[]}>} variants
   * @returns {object | undefined}
   */
  #findSizePanelSingleVariant(item, variants) {
    const panel = this.#sizePanel;
    const sizeSelect = item.querySelector('[data-product-card-size-option]');

    if (!(sizeSelect instanceof HTMLSelectElement)) {
      const variantInput = item.querySelector('[data-product-card-size-panel-variant-id]');
      if (!(variantInput instanceof HTMLInputElement)) return undefined;

      return variants.find((variant) => String(variant.id) === String(variantInput.value)) || variants[0];
    }

    this.#syncSizePanelOptionWidth(sizeSelect);

    const selectedSize = sizeSelect.value;
    const sizeOptionPosition = Number.parseInt(
      sizeSelect.dataset.sizeOptionPosition || panel?.dataset.sizeOptionPosition || '0',
      10,
    );
    const colorOptionPosition = Number.parseInt(panel?.dataset.colorOptionPosition || '', 10);
    const selectedColor = panel?.dataset.selectedColor || '';

    const matchesSize = (/** @type {{option1: string|null, option2: string|null, option3: string|null, options?: string[], isHiddenSize?: boolean}} */ variant) =>
      !variant.isHiddenSize && this.#getSizePanelOptionValue(variant, sizeOptionPosition) === selectedSize;

    if (!Number.isNaN(colorOptionPosition) && colorOptionPosition > 0 && selectedColor) {
      const withColor = variants.find(
        (variant) =>
          matchesSize(variant) &&
          this.#getSizePanelOptionValue(variant, colorOptionPosition - 1) === selectedColor,
      );
      if (withColor) return withColor;
    }

    return variants.find(matchesSize);
  }

  /**
   * Hides size select options whose matching color+size variant is marked redesign_is_hidden_size.
   * @param {HTMLElement} item
   * @param {Array<{id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null, options?: string[], isHiddenSize?: boolean}>} variants
   */
  #syncSizePanelHiddenOptions(item, variants) {
    const panel = this.#sizePanel;
    const colorOptionPosition = Number.parseInt(panel?.dataset.colorOptionPosition || '', 10);
    const selectedColor = panel?.dataset.selectedColor || '';
    const sizeSelect = item.querySelector('[data-product-card-size-option]');

    if (sizeSelect instanceof HTMLSelectElement) {
      const sizeOptionPosition = Number.parseInt(
        sizeSelect.dataset.sizeOptionPosition || panel?.dataset.sizeOptionPosition || '0',
        10,
      );
      let firstVisibleValue = '';

      for (const option of sizeSelect.options) {
        const sizeValue = option.value;
        const matched = variants.find((variant) => {
          if (this.#getSizePanelOptionValue(variant, sizeOptionPosition) !== sizeValue) return false;
          if (!Number.isNaN(colorOptionPosition) && colorOptionPosition > 0 && selectedColor) {
            return this.#getSizePanelOptionValue(variant, colorOptionPosition - 1) === selectedColor;
          }
          return true;
        });
        const hide = Boolean(matched?.isHiddenSize);
        option.hidden = hide;
        if (!hide && !firstVisibleValue) firstVisibleValue = sizeValue;
      }

      const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
      if (selectedOption?.hidden && firstVisibleValue) {
        sizeSelect.value = firstVisibleValue;
      }
      return;
    }

    if (!item.hasAttribute('data-product-card-split-size')) return;

    const waistSelect = item.querySelector('[data-size-axis="waist"]');
    const lengthSelect = item.querySelector('[data-size-axis="length"]');
    if (!(waistSelect instanceof HTMLSelectElement) || !(lengthSelect instanceof HTMLSelectElement)) return;

    const sizeOptionPosition = Number.parseInt(
      waistSelect.dataset.sizeOptionPosition || panel?.dataset.sizeOptionPosition || '0',
      10,
    );

    const visibleForColor = variants.filter((variant) => {
      if (variant.isHiddenSize) return false;
      if (!Number.isNaN(colorOptionPosition) && colorOptionPosition > 0 && selectedColor) {
        return this.#getSizePanelOptionValue(variant, colorOptionPosition - 1) === selectedColor;
      }
      return true;
    });

    const waists = new Set();
    const lengths = new Set();
    for (const variant of visibleForColor) {
      const optionValue = this.#getSizePanelOptionValue(variant, sizeOptionPosition);
      if (!optionValue || !optionValue.includes('/')) continue;
      const [waist = '', length = ''] = optionValue.split('/').map((part) => part.trim());
      if (waist) waists.add(waist);
      if (length) lengths.add(length);
    }

    for (const option of waistSelect.options) {
      option.hidden = option.value !== '' && !waists.has(option.value);
    }
    for (const option of lengthSelect.options) {
      option.hidden = option.value !== '' && !lengths.has(option.value);
    }

    if (waistSelect.selectedOptions[0]?.hidden) {
      const next = Array.from(waistSelect.options).find((option) => !option.hidden);
      if (next) waistSelect.value = next.value;
    }
    if (lengthSelect.selectedOptions[0]?.hidden) {
      const next = Array.from(lengthSelect.options).find((option) => !option.hidden);
      if (next) lengthSelect.value = next.value;
    }
  }

  #syncSizePanelVariant() {
    const panel = this.#sizePanel;
    if (!(panel instanceof HTMLDialogElement)) return;

    const item = panel.querySelector('[data-product-card-size-panel-item]');
    if (!(item instanceof HTMLElement)) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (
      item.querySelector('[data-product-card-size-panel-variant-id]')
    );
    const submitButton = /** @type {HTMLButtonElement | null} */ (
      item.querySelector('[data-product-card-size-panel-submit]')
    );
    const variantsScript = item.querySelector('[data-product-card-size-panel-variants]');

    if (!variantInput || !submitButton || !(variantsScript instanceof HTMLScriptElement)) {
      return;
    }

    if (submitButton.getAttribute('aria-busy') === 'true') return;

    /** @type {Array<{id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null, options?: string[]}>} */
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent || '[]');
    } catch {
      return;
    }

    const isSplit = item.hasAttribute('data-product-card-split-size');
    this.#syncSizePanelHiddenOptions(item, variants);
    const matchedVariant = isSplit
      ? this.#findSizePanelSplitVariant(item, variants)
      : this.#findSizePanelSingleVariant(item, variants);

    const addLabel = item.dataset.addLabel || 'Add to cart';
    const soldOutLabel = item.dataset.soldOutLabel || 'Sold out';

    if (!matchedVariant) {
      variantInput.disabled = true;
      submitButton.disabled = true;
      submitButton.textContent = soldOutLabel;
      this.#syncSizePanelPdpLinks(variantInput.value);
      this.#updateSizePanelMedia();
      return;
    }

    variantInput.value = String(matchedVariant.id);
    variantInput.disabled = !matchedVariant.available;
    submitButton.disabled = !matchedVariant.available;
    submitButton.textContent = matchedVariant.available ? addLabel : soldOutLabel;
    this.#syncSizePanelPdpLinks(matchedVariant.id);
    this.#updateSizePanelMedia();
  }

  /**
   * Keep size-panel image/title links pointed at the currently matched variant.
   * @param {string | number | null | undefined} variantId
   */
  #syncSizePanelPdpLinks(variantId) {
    const panel = this.#sizePanel;
    if (!(panel instanceof HTMLDialogElement)) return;

    const url = this.#variantProductUrl(variantId);
    if (!url) return;

    for (const link of panel.querySelectorAll('[data-product-card-size-panel-pdp-link]')) {
      if (link instanceof HTMLAnchorElement) {
        link.href = url.href;
      }
    }
  }

  /**
   * @param {HTMLSelectElement} input
   */
  #syncSizePanelOptionWidth(input) {
    const selectedOption = input.options[input.selectedIndex];
    const selectedLabel = selectedOption?.textContent?.trim() || input.value || '';
    const widthEm = Math.max(selectedLabel.length * 0.65, 1);

    input.style.setProperty('--product-card-size-ch', `${widthEm}em`);
  }

  /**
   * @param {HTMLButtonElement} submitButton
   */
  async #addSizePanelToCart(submitButton) {
    this.#syncSizePanelVariant();

    const panel = this.#sizePanel;
    if (!(panel instanceof HTMLDialogElement)) return;

    const item = panel.querySelector('[data-product-card-size-panel-item]');
    if (!(item instanceof HTMLElement)) return;

    const form = /** @type {HTMLFormElement | null} */ (item.querySelector('.product-card-dstrezzed__size-panel-form'));
    const variantInput = /** @type {HTMLInputElement | null} */ (
      item.querySelector('[data-product-card-size-panel-variant-id]')
    );

    if (!form || !variantInput) return;
    if (submitButton.disabled || variantInput.disabled || submitButton.getAttribute('aria-busy') === 'true') {
      return;
    }

    const formData = new FormData(form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    for (const cartItemsComponent of cartItemsComponents) {
      if (!(cartItemsComponent instanceof HTMLElement) || !cartItemsComponent.dataset.sectionId) continue;
      sectionIds.push(cartItemsComponent.dataset.sectionId);
    }

    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');

    try {
      const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(cartAddUrl, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        submitButton.disabled = variantInput.disabled;
        submitButton.removeAttribute('aria-busy');
        this.#syncSizePanelVariant();
        return;
      }

      const productId = item.dataset.productCardSizePanelProductId || this.dataset.productId || '';
      document.dispatchEvent(
        new CartAddEvent({}, `product-card-size-panel-${productId}`, {
          source: 'product-form-component',
          itemCount: 1,
          productId,
          sections: data.sections || {},
        }),
      );

      this.#closeSizePanel();
    } catch {
      submitButton.disabled = variantInput.disabled;
      submitButton.removeAttribute('aria-busy');
      this.#syncSizePanelVariant();
      return;
    }

    submitButton.removeAttribute('aria-busy');
    this.#syncSizePanelVariant();
  }

  /**
   * One-time expand of limited color swatches with a slide-right animation.
   * Moves +N to the end of the swatch row, animates the reveal, then removes +N.
   * @param {HTMLButtonElement} overflowButton
   */
  #revealDstrezzedColorSwatches(overflowButton) {
    const wrapper = overflowButton.closest('.product-card-dstrezzed__color-swatches-wrapper');
    const container = wrapper?.querySelector('.product-card-dstrezzed__color-swatches');
    if (!(wrapper instanceof HTMLElement) || !(container instanceof HTMLElement)) return;
    if (
      container.classList.contains('product-card-dstrezzed__color-swatches--expanded') ||
      container.classList.contains('product-card-dstrezzed__color-swatches--animating')
    ) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const durationMs = 420;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

    const firstOverflowRect = overflowButton.getBoundingClientRect();
    const firstContainerWidth = container.getBoundingClientRect().width;

    container.appendChild(overflowButton);
    wrapper.classList.remove('product-card-dstrezzed__color-swatches-wrapper--limited');
    container.classList.add('product-card-dstrezzed__color-swatches--expanded');
    container.classList.add('product-card-dstrezzed__color-swatches--animating');

    const finishExpand = () => {
      container.classList.remove('product-card-dstrezzed__color-swatches--animating');
      container.style.maxWidth = '';
      container.style.overflowX = '';
      container.style.transition = '';
      overflowButton.style.transform = '';
      overflowButton.style.opacity = '';
      overflowButton.style.transition = '';
      overflowButton.remove();

      document.dispatchEvent(
        new CustomEvent('product-card:dstrezzed-swatches-expanded', {
          bubbles: true,
          detail: { container },
        })
      );
    };

    if (prefersReducedMotion) {
      finishExpand();
      return;
    }

    const lastOverflowRect = overflowButton.getBoundingClientRect();
    const overflowDeltaX = firstOverflowRect.left - lastOverflowRect.left;
    const targetWidth = Math.max(wrapper.getBoundingClientRect().width, firstContainerWidth);

    container.style.maxWidth = `${firstContainerWidth}px`;
    container.style.overflowX = 'hidden';
    overflowButton.style.transform = `translateX(${overflowDeltaX}px)`;
    overflowButton.style.opacity = '1';
    overflowButton.style.transition = 'none';

    // Double rAF so the browser commits the inverted start frame before transitioning.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.style.transition = `max-width ${durationMs}ms ${easing}`;
        container.style.maxWidth = `${targetWidth}px`;

        overflowButton.style.transition = `transform ${durationMs}ms ${easing}, opacity ${Math.round(durationMs * 0.35)}ms ${easing} ${Math.round(durationMs * 0.65)}ms`;
        overflowButton.style.transform = 'translateX(0)';
        overflowButton.style.opacity = '0';
      });
    });

    let finished = false;
    const onFinish = () => {
      if (finished) return;
      finished = true;
      finishExpand();
    };

    container.addEventListener('transitionend', (event) => {
      if (event.target === container && event.propertyName === 'max-width') {
        onFinish();
      }
    });
    window.setTimeout(onFinish, durationMs + 80);
  }

  /**
   * Updates card price and links for the selected color variant.
   * @param {string} variantId
   * @param {string} colorValue
   * @param {string} [productUrl]
   */
  async #updateDstrezzedCardForColor(variantId, colorValue, productUrl) {
    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) return;

    this.#dstrezzedSwatchAbortController?.abort();
    this.#dstrezzedSwatchAbortController = new AbortController();

    let targetUrl;
    try {
      targetUrl = new URL(productUrl || link.href, window.location.origin);
    } catch {
      return;
    }
    targetUrl.searchParams.set('variant', variantId);

    const requestUrl = new URL(targetUrl.href);
    requestUrl.searchParams.set('section_id', 'section-rendering-product-card');

    try {
      const response = await fetch(requestUrl.toString(), { signal: this.#dstrezzedSwatchAbortController.signal });
      if (!response.ok) {
        throw new Error(`Failed to load product card variant: ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const eventLike = {
        detail: {
          resource: { id: variantId },
          data: { html },
        },
      };

      this.updatePrice(/** @type {VariantUpdateEvent} */ (eventLike));
      this.#updateProductUrl(/** @type {VariantUpdateEvent} */ (eventLike));
      this.#updateDstrezzedProductBadges(html);
      this.#updateDstrezzedCardGallery(html);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    }
  }

  /**
   * Replaces gallery overlay + slideshow markup for the selected color, then
   * re-applies Model/Product lead visibility (indexes 0/1) and overlay state.
   * @param {Document} html
   */
  #updateDstrezzedCardGallery(html) {
    const cardGallery = this.refs.cardGallery;
    if (!(cardGallery instanceof HTMLElement)) return;

    const updatedCard = this.#querySectionRenderingProductCard(html);
    const updatedGallery = updatedCard?.querySelector('[ref="cardGallery"]') || html.querySelector('product-card [ref="cardGallery"]');
    if (!(updatedGallery instanceof HTMLElement)) return;

    const initialShownImage = cardGallery.getAttribute('data-initial-shown-image');

    const overlay = cardGallery.querySelector('.card-gallery__view-overlay');
    const updatedOverlay = updatedGallery.querySelector('.card-gallery__view-overlay');
    const galleryLink = cardGallery.querySelector('[ref="cardGalleryLink"]');
    const updatedGalleryLink = updatedGallery.querySelector('[ref="cardGalleryLink"]');

    if (overlay instanceof HTMLElement && updatedOverlay instanceof HTMLElement) {
      morph(overlay, updatedOverlay);
    } else if (!(overlay instanceof HTMLElement) && updatedOverlay instanceof HTMLElement && galleryLink) {
      galleryLink.before(updatedOverlay.cloneNode(true));
    } else if (overlay instanceof HTMLElement && !(updatedOverlay instanceof HTMLElement)) {
      overlay.remove();
    }

    if (galleryLink instanceof HTMLAnchorElement && updatedGalleryLink instanceof HTMLAnchorElement) {
      const slideshow = galleryLink.querySelector('slideshow-component');
      const updatedSlideshow = updatedGalleryLink.querySelector('slideshow-component');
      const slides = slideshow?.querySelector('slideshow-slides');
      const updatedSlides = updatedSlideshow?.querySelector('slideshow-slides');
      const currentArrows = slideshow?.querySelector('slideshow-arrows');
      const updatedArrows = updatedSlideshow?.querySelector('slideshow-arrows');
      const arrowPresenceChanged = Boolean(currentArrows) !== Boolean(updatedArrows);

      if (
        !arrowPresenceChanged &&
        slideshow instanceof HTMLElement &&
        updatedSlideshow instanceof HTMLElement &&
        slides &&
        updatedSlides
      ) {
        // Keep existing arrow controls (style/shape) and refresh only media slides.
        // This preserves Dstrezzed chevron icons while still re-rendering relevant images.
        // Client-only attrs (e.g. in-viewport) must survive — stripping in-viewport leaves
        // overflow:hidden and blocks mobile swipe until the card leaves/re-enters the viewport.
        const preservedSlideshowAttributes = new Set(['in-viewport', 'actioned']);
        for (const { name, value } of updatedSlideshow.attributes) {
          slideshow.setAttribute(name, value);
        }
        for (const { name } of [...slideshow.attributes]) {
          if (preservedSlideshowAttributes.has(name)) continue;
          if (!updatedSlideshow.hasAttribute(name)) {
            slideshow.removeAttribute(name);
          }
        }
        morph(slides, updatedSlides);
      } else {
        // Keep badges/wishlist/size selector children; refresh gallery link subtree.
        // Also used when overlay-only ↔ multi-image color switches change arrow presence.
        morph(galleryLink, updatedGalleryLink);
      }
    } else {
      morph(cardGallery, updatedGallery);
      if (initialShownImage) {
        cardGallery.setAttribute('data-initial-shown-image', initialShownImage);
      }
    }

    // Color-filtered markup already includes Model/Product leads + rest images;
    // sync overlay visibility, hide leads on desktop, and select first visible slide.
    this.#syncDstrezzedCardView({ resetSlideshow: true });
    this.#wireMobileQuickAddTrigger();
    this.#syncQuickAddLeadToPdpControls();
  }

  /**
   * Updates size selector variant ids/availability for selected color.
   * @param {string} colorValue
   */
  #updateDstrezzedSizeSelector(colorValue) {
    const selector = this.querySelector('.product-card-dstrezzed__size-selector');
    if (!(selector instanceof HTMLElement)) return;

    const variantsScript = selector.querySelector('.product-card-dstrezzed__variants-data');
    if (!(variantsScript instanceof HTMLScriptElement) || !variantsScript.textContent) return;

    const colorOptionPosition = Number.parseInt(selector.dataset.colorOptionPosition || '', 10);
    const sizeOptionPosition = Number.parseInt(selector.dataset.sizeOptionPosition || '', 10);
    if (Number.isNaN(sizeOptionPosition)) return;

    /** @type {Array<{id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null, isHiddenSize?: boolean}>} */
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch {
      return;
    }

    const optionAtPosition = (
      /** @type {{option1: string|null, option2: string|null, option3: string|null}} */ variant,
      /** @type {number} */ position,
    ) => {
      if (position === 1) return variant.option1;
      if (position === 2) return variant.option2;
      if (position === 3) return variant.option3;
      return null;
    };

    selector.dataset.selectedColor = colorValue;

    const sizeForms = selector.querySelectorAll('.product-card-dstrezzed__size-form');
    for (const sizeForm of sizeForms) {
      if (!(sizeForm instanceof HTMLElement)) continue;

      const sizeValue = sizeForm.dataset.sizeValue;
      if (!sizeValue) continue;

      const variantInput = /** @type {HTMLInputElement | null} */ (sizeForm.querySelector('input[name="id"]'));
      const sizeButton = /** @type {HTMLButtonElement | null} */ (
        sizeForm.querySelector('.product-card-dstrezzed__size-button')
      );
      if (!variantInput || !sizeButton) continue;

      const matchedVariant = variants.find((variant) => {
        const sizeMatches = optionAtPosition(variant, sizeOptionPosition) === sizeValue;
        if (!sizeMatches) return false;

        if (Number.isNaN(colorOptionPosition)) return true;

        return optionAtPosition(variant, colorOptionPosition) === colorValue;
      });

      if (matchedVariant?.isHiddenSize) {
        sizeForm.hidden = true;
        sizeButton.disabled = true;
        sizeButton.classList.add('product-card-dstrezzed__size-button--unavailable');
        sizeButton.setAttribute('data-missing-variant', '');
        continue;
      }

      sizeForm.hidden = false;

      if (matchedVariant) {
        variantInput.value = String(matchedVariant.id);
        sizeButton.disabled = this.#quickAddLeadsToPdp ? false : !matchedVariant.available;
        sizeButton.classList.toggle('product-card-dstrezzed__size-button--unavailable', !matchedVariant.available);
        sizeButton.removeAttribute('data-missing-variant');
      } else {
        sizeButton.disabled = this.#quickAddLeadsToPdp ? false : true;
        sizeButton.classList.add('product-card-dstrezzed__size-button--unavailable');
        sizeButton.setAttribute('data-missing-variant', '');
      }
    }
  }

  /**
   * Updates mobile quick add variant id/availability for selected color.
   * @param {string} colorValue
   * @param {string | undefined} fallbackVariantId
   */
  #updateDstrezzedMobileQuickAdd(colorValue, fallbackVariantId) {
    const quickAddForm = this.querySelector('.product-card-dstrezzed__mobile-quick-add-form');
    if (!(quickAddForm instanceof HTMLElement)) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (quickAddForm.querySelector('input[name="id"]'));
    const quickAddButton = /** @type {HTMLButtonElement | null} */ (
      quickAddForm.querySelector('.product-card-dstrezzed__mobile-quick-add')
    );
    if (!variantInput || !quickAddButton) return;

    const selector = this.querySelector('.product-card-dstrezzed__size-selector');
    if (!(selector instanceof HTMLElement)) {
      if (fallbackVariantId) variantInput.value = fallbackVariantId;
      return;
    }

    const variantsScript = selector.querySelector('.product-card-dstrezzed__variants-data');
    const colorOptionPosition = Number.parseInt(selector.dataset.colorOptionPosition || '', 10);
    if (!(variantsScript instanceof HTMLScriptElement) || !variantsScript.textContent || Number.isNaN(colorOptionPosition)) {
      if (fallbackVariantId) variantInput.value = fallbackVariantId;
      return;
    }

    /** @type {Array<{id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null}>} */
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch {
      if (fallbackVariantId) variantInput.value = fallbackVariantId;
      return;
    }

    const optionAtPosition = (
      /** @type {{option1: string|null, option2: string|null, option3: string|null}} */ variant,
      /** @type {number} */ position,
    ) => {
      if (position === 1) return variant.option1;
      if (position === 2) return variant.option2;
      if (position === 3) return variant.option3;
      return null;
    };

    const matchingVariants = variants.filter(
      (variant) => !variant.isHiddenSize && optionAtPosition(variant, colorOptionPosition) === colorValue,
    );
    const selectedVariant = matchingVariants.find((variant) => variant.available) || matchingVariants[0];

    if (selectedVariant) {
      variantInput.value = String(selectedVariant.id);
      quickAddButton.disabled = this.#quickAddLeadsToPdp ? false : !selectedVariant.available;
    } else if (fallbackVariantId) {
      variantInput.value = fallbackVariantId;
      quickAddButton.disabled = false;
    }
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.#previousSlideIndex = null;
    this.#resetVariant();
  }

  /**
   * Desktop Dstrezzed cards: return to the first visible slideshow slide on mouse leave.
   * @param {PointerEvent} event - The pointer event.
   */
  resetDstrezzedCardGallery(event) {
    if (event.pointerType !== 'mouse') return;

    const gallery = this.refs.cardGallery;
    if (
      gallery instanceof HTMLElement &&
      (gallery.querySelector(':scope > .card-gallery__view-overlay--standalone') ||
        gallery.querySelector('slideshow-component[data-dstrezzed-overlay-only]'))
    ) {
      return;
    }

    const { slideshow } = this.refs;
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const firstVisible = slideshow.slides?.[0];
    if (!(firstVisible instanceof HTMLElement)) return;

    const slideId = firstVisible.getAttribute('slide-id');
    if (!slideId) return;

    this.#previousSlideIndex = null;
    slideshow.select({ id: slideId }, undefined, { animate: false, force: true });
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    // Size panel: only explicit image/title PDP links navigate; ignore surface/backdrop clicks.
    if (event.target.closest('[data-product-card-size-panel]')) {
      if (!event.target.closest('[data-product-card-size-panel-pdp-link]')) return;
    }

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      // Stamp page + hash synchronously so browser back restores the correct PLP entry.
      // Deferred replaceState can lose to navigation and drop the hash / page param.
      history.replaceState({}, '', url.toString());

      try {
        const storageUrl = new URL(url.toString());
        storageUrl.hash = '';
        const grid = infiniteResultsList.querySelector('[ref="grid"], .product-grid');
        const productsPerPage = Number(grid?.dataset.productsPerPage || 24);
        const parseAfterCsv = (csv) =>
          (csv || '')
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((n) => Number.isFinite(n) && n > 0);
        const promoAfter = parseAfterCsv(grid?.dataset.plpPromoAfter);
        const promoAfterMobile = parseAfterCsv(
          grid?.dataset.plpPromoAfterMobile || grid?.dataset.plpPromoAfter
        );

        /** @type {Record<string, number>} */
        const promoHeights = {};
        if (grid) {
          for (const promo of grid.querySelectorAll('.product-grid__item--promo[data-promo-after]')) {
            if (!(promo instanceof HTMLElement)) continue;
            const afterKey = promo.getAttribute('data-promo-after');
            if (!afterKey) continue;
            const measured = Number.parseFloat(promo.style.getPropertyValue('--collection-promo-height'));
            if (Number.isFinite(measured) && measured > 0) {
              promoHeights[afterKey] = measured;
            }
          }
        }

        sessionStorage.setItem(
          'dstrezzed-plp-scroll',
          JSON.stringify({
            key: `${storageUrl.pathname}${storageUrl.search}`,
            scrollY: window.scrollY,
            documentHeight: document.documentElement.scrollHeight,
            page: Number(parent?.dataset.page || url.searchParams.get('page') || 1),
            productsPerPage,
            promoAfter,
            promoAfterMobile,
            promoHeights,
            hash: productCardAnchor,
          })
        );
      } catch {
        // sessionStorage may be unavailable
      }
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

/**
 * Enables grab-to-scroll (with momentum) on Dstrezzed product-card size selectors
 * and expanded color-swatch rows when the hidden-scrollbar row overflows.
 * Toggles `.scrollable-left` / `.scrollable-right` for directional fade hints on the wrapper.
 */
function initDstrezzedSizeSelectorScroll() {
  const DRAG_THRESHOLD = 6;
  const FRICTION = 0.95;
  const MIN_VELOCITY = 0.05;
  const SCROLLABLE_LEFT_CLASS = 'scrollable-left';
  const SCROLLABLE_RIGHT_CLASS = 'scrollable-right';
  const SELECTOR =
    '.product-card-dstrezzed__size-selector, .product-card-dstrezzed__color-swatches--expanded';

  /** @type {HTMLElement | null} */
  let scrollEl = null;
  let isPointerDown = false;
  let isDragging = false;
  let suppressClick = false;
  let startX = 0;
  let startScrollLeft = 0;
  let lastX = 0;
  let lastTime = 0;
  let velocity = 0;
  let momentumId = 0;
  let scrollableTicking = false;

  /**
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  const hasHorizontalOverflow = (container) => container.scrollWidth > container.clientWidth + 1;

  /**
   * @param {HTMLElement} container
   * @returns {{ left: boolean, right: boolean }}
   */
  const getScrollableEdges = (container) => {
    if (!hasHorizontalOverflow(container)) {
      return { left: false, right: false };
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    return {
      left: container.scrollLeft > 1,
      right: container.scrollLeft < maxScrollLeft - 1,
    };
  };

  const updateScrollableClass = () => {
    const containers = document.querySelectorAll(SELECTOR);

    for (const container of containers) {
      if (!(container instanceof HTMLElement)) continue;

      const { left, right } = getScrollableEdges(container);
      container.classList.toggle(SCROLLABLE_LEFT_CLASS, left);
      container.classList.toggle(SCROLLABLE_RIGHT_CLASS, right);
    }
  };

  const requestScrollableUpdate = () => {
    if (scrollableTicking) return;

    scrollableTicking = true;
    requestAnimationFrame(() => {
      scrollableTicking = false;
      observeScrollableTargets();
      updateScrollableClass();
    });
  };

  const resizeObserver = new ResizeObserver(requestScrollableUpdate);

  const observeScrollableTargets = () => {
    const containers = document.querySelectorAll(SELECTOR);

    for (const container of containers) {
      resizeObserver.observe(container);
    }
  };

  const stopMomentum = () => {
    if (!momentumId) return;
    cancelAnimationFrame(momentumId);
    momentumId = 0;
  };

  const startMomentum = () => {
    stopMomentum();

    const tick = () => {
      if (!scrollEl || Math.abs(velocity) < MIN_VELOCITY) {
        stopMomentum();
        requestScrollableUpdate();
        return;
      }

      scrollEl.scrollLeft -= velocity * 16;
      velocity *= FRICTION;
      requestScrollableUpdate();
      momentumId = requestAnimationFrame(tick);
    };

    momentumId = requestAnimationFrame(tick);
  };

  const cleanupPointerListeners = () => {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', endDrag);
    document.removeEventListener('pointercancel', endDrag);
  };

  const endDrag = () => {
    if (!isPointerDown) return;

    const wasDragging = isDragging;

    isPointerDown = false;
    isDragging = false;
    scrollEl?.classList.remove('is-dragging');
    cleanupPointerListeners();
    requestScrollableUpdate();

    if (!wasDragging) return;

    startMomentum();
    suppressClick = true;
    setTimeout(() => {
      suppressClick = false;
    }, 0);
  };

  /**
   * @param {PointerEvent} event
   */
  const onPointerMove = (event) => {
    if (!isPointerDown || !scrollEl) return;

    const x = event.clientX;
    const now = performance.now();
    const deltaX = x - startX;

    if (!isDragging) {
      if (Math.abs(deltaX) <= DRAG_THRESHOLD) return;

      isDragging = true;
      scrollEl.classList.add('is-dragging');
    }

    const elapsed = Math.max(now - lastTime, 1);
    velocity = (x - lastX) / elapsed;
    lastX = x;
    lastTime = now;

    scrollEl.scrollLeft = startScrollLeft - deltaX;
    requestScrollableUpdate();
    event.preventDefault();
  };

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const container = target.closest(SELECTOR);
    if (!(container instanceof HTMLElement)) return;
    if (!hasHorizontalOverflow(container)) return;

    stopMomentum();
    scrollEl = container;
    isPointerDown = true;
    isDragging = false;
    suppressClick = false;
    startX = event.clientX;
    startScrollLeft = container.scrollLeft;
    lastX = startX;
    lastTime = performance.now();
    velocity = 0;

    document.addEventListener('pointermove', onPointerMove, { passive: false });
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest(SELECTOR)) return;

      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    },
    true
  );

  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches(SELECTOR)) return;
      requestScrollableUpdate();
    },
    { capture: true, passive: true }
  );

  window.addEventListener('resize', requestScrollableUpdate, { passive: true });
  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    observeProductGrids();
    requestScrollableUpdate();
  });
  document.addEventListener('product-card:dstrezzed-swatches-expanded', requestScrollableUpdate);

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR)) {
          requestScrollableUpdate();
          return;
        }
      }
    }
  });

  const observeProductGrids = () => {
    const grids = document.querySelectorAll('.product-grid');

    for (const grid of grids) {
      mutationObserver.observe(grid, { childList: true, subtree: true });
    }
  };

  observeProductGrids();
  observeScrollableTargets();
  updateScrollableClass();
}

initDstrezzedSizeSelectorScroll();
